import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Input, ModalWindow, Text, useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { invalidateSignedDocumentQueries } from '../../utils/invalidate_signed_document_queries';
import { isEmailFixedForSignerType } from '../site_document_details.helpers';
const INVITE_ROUTE = '/v2/routes/econsent/invite';
const RESEND_INVITE_ROUTE = '/v2/routes/econsent/resend_invite';
/** Mirrors the `required|email` rule classic put on this field. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/**
 * Invites a signer to a role that has none, or re-sends an existing invite — classic's
 * "Assign or Resend" row action and its `InviteForm` dialog.
 *
 * Which of the two it is comes from the row: a role with no invite is an assignment
 * (`POST invite`, creating one), a role with an invite is a re-send
 * (`PUT resend_invite`, reusing it), exactly as classic branched on the invite id.
 */
export const useSignerInviteAction = ({ apiClient, documentId, documentTemplateId, getApiErrorMessage, }) => {
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const queryClient = useQueryClient();
    const [selectedRow, setSelectedRow] = useState(null);
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const openSignerInvite = useCallback((row) => {
        setSelectedRow(row);
        setEmail(row.email);
    }, []);
    const close = useCallback(() => {
        setSelectedRow(null);
        setEmail('');
        setIsSubmitting(false);
    }, []);
    const isAssign = !!selectedRow?.isUnassigned;
    // Keyed off the *stored* address, not the live input: classic showed the participant's
    // own address read-only because it had one to show. A role with no invite yet has none,
    // and locking an empty field there leaves a dialog that can never be submitted.
    const isEmailFixed = isEmailFixedForSignerType(selectedRow?.signerType) && !!selectedRow?.email;
    const isEmailValid = EMAIL_PATTERN.test(email.trim());
    const submit = useCallback(async () => {
        if (!selectedRow || !apiClient || !isEmailValid) {
            return;
        }
        setIsSubmitting(true);
        try {
            if (isAssign) {
                await apiClient.post(INVITE_ROUTE, {
                    ec__email: email.trim(),
                    ec__signer_role: selectedRow.signerRole,
                    ec__document_template: documentTemplateId,
                    ec__signed_document: documentId,
                });
            }
            else {
                await apiClient.put(RESEND_INVITE_ROUTE, {
                    ec__email: email.trim(),
                    ec__document_invite: selectedRow.id,
                });
            }
            addNotification({
                title: '',
                subtitle: translate('info.site_document_details.invitation_sent'),
                type: 'success',
            });
            // An assignment creates an invite, so the signers table has to re-read.
            invalidateSignedDocumentQueries(queryClient, { documentId });
            close();
        }
        catch (error) {
            addNotification({
                title: '',
                subtitle: getApiErrorMessage?.(error) ?? translate('error.site_document_details.invite_failed'),
                type: 'error',
            });
            setIsSubmitting(false);
        }
    }, [
        addNotification,
        apiClient,
        close,
        documentId,
        documentTemplateId,
        email,
        getApiErrorMessage,
        isAssign,
        isEmailValid,
        queryClient,
        selectedRow,
        translate,
    ]);
    const signerInviteModal = useMemo(() => selectedRow
        ? createPortal(_jsx(ModalWindow, { isOpen: true, title: isAssign
                ? translate('label.site_document_details.assign_signer')
                : translate('label.site_document_details.resend_invite'), closeWindow: close, width: 'base', dataTestId: 'signer-invite-modal', footerPrimaryActionButton: {
                label: translate('action.site_document_details.send_invite'),
                onClick: submit,
                disabled: !isEmailValid || isSubmitting,
                dataTestId: 'signer-invite-submit',
            }, footerSecondaryActionButtons: [
                { label: translate('generic.cancel'), onClick: close, disabled: isSubmitting },
            ], children: _jsxs("div", { className: 'flex flex-col gap-4 py-4', children: [_jsx(Text, { size: 'sm', className: 'text-text-secondary', children: selectedRow.signerRole }), _jsx(Input, { id: 'signer-invite-email', label: translate('label.site_document_details.signers.email'), value: email, onChange: (event) => setEmail(event.target.value), 
                        // Nucleus Input has no readOnly; disabled is how it renders non-editable.
                        disabled: isEmailFixed, required: true, dataTestId: 'signer-invite-email' })] }) }), document.body)
        : null, [close, email, isAssign, isEmailFixed, isEmailValid, isSubmitting, selectedRow, submit, translate]);
    return { openSignerInvite, signerInviteModal };
};
